import React from 'react';

/**
 * Render the right Bootstrap input control for a setting based on its
 * `input_type`. Stays controlled — the caller manages the value and keeps
 * it in editor format (string for text inputs, boolean for switches,
 * array of values for multiselects, etc.).
 *
 * The `definition` prop accepts the raw definition row including
 * `input_type`, `data_type`, `options`, `placeholder`, validation hints.
 */
export default function SettingValueEditor({ definition, value, onChange, disabled = false }) {
  const {
    input_type: inputType,
    data_type: dataType,
    options = [],
    placeholder,
    min_value: minValue,
    max_value: maxValue,
    min_length: minLength,
    max_length: maxLength,
    pattern,
    setting_key: settingKey,
  } = definition || {};

  const idBase = `ci-setting-input-${settingKey || Math.random().toString(36).slice(2, 8)}`;

  switch (inputType) {
    case 'switch':
    case 'checkbox': {
      const checked = Boolean(value);
      const isSwitch = inputType === 'switch';
      return (
        <div className={`form-check ${isSwitch ? 'form-switch' : ''}`}>
          <input
            type="checkbox"
            className="form-check-input"
            id={idBase}
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={idBase}>
            {checked ? 'On' : 'Off'}
          </label>
        </div>
      );
    }
    case 'select': {
      return (
        <select
          className="form-select"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        >
          <option value="">— Select —</option>
          {options.map((opt, idx) => (
            <option value={opt.value} key={`${opt.value ?? ''}-${idx}`}>
              {opt.label || opt.value}
            </option>
          ))}
        </select>
      );
    }
    case 'multiselect': {
      const selected = new Set(Array.isArray(value) ? value.map(String) : []);
      return (
        <div className="ci-multiselect d-flex flex-wrap gap-2">
          {options.length === 0 ? (
            <span className="text-muted small">No options defined.</span>
          ) : null}
          {options.map((opt, idx) => {
            const v = String(opt.value ?? '');
            const isOn = selected.has(v);
            return (
              <button
                type="button"
                key={`${v}-${idx}`}
                className={`btn btn-sm ${isOn ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => {
                  const next = new Set(selected);
                  if (next.has(v)) next.delete(v);
                  else next.add(v);
                  onChange?.(Array.from(next));
                }}
                disabled={disabled}
              >
                {opt.label || opt.value}
              </button>
            );
          })}
        </div>
      );
    }
    case 'radio': {
      return (
        <div className="d-flex flex-column gap-1">
          {options.map((opt, idx) => {
            const v = opt.value;
            const id = `${idBase}-radio-${idx}`;
            return (
              <div className="form-check" key={id}>
                <input
                  type="radio"
                  className="form-check-input"
                  id={id}
                  checked={String(value ?? '') === String(v ?? '')}
                  onChange={() => onChange?.(v)}
                  disabled={disabled}
                />
                <label className="form-check-label" htmlFor={id}>
                  {opt.label || opt.value}
                </label>
              </div>
            );
          })}
        </div>
      );
    }
    case 'textarea':
    case 'json': {
      const isJson = inputType === 'json';
      return (
        <textarea
          className={`form-control ${isJson ? 'font-monospace' : ''}`}
          rows={isJson ? 6 : 3}
          value={value ?? ''}
          placeholder={placeholder || (isJson ? '{ }' : '')}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'number':
    case 'decimal': {
      const step = inputType === 'decimal' ? 'any' : '1';
      return (
        <input
          type="number"
          className="form-control"
          value={value ?? ''}
          min={minValue ?? undefined}
          max={maxValue ?? undefined}
          step={step}
          placeholder={placeholder || ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'date': {
      return (
        <input
          type="date"
          className="form-control"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'datetime': {
      return (
        <input
          type="datetime-local"
          className="form-control"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'time': {
      return (
        <input
          type="time"
          className="form-control"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'color': {
      return (
        <div className="d-flex align-items-center gap-2">
          <input
            type="color"
            className="form-control form-control-color"
            value={value || '#000000'}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
          />
          <code className="small">{value || ''}</code>
        </div>
      );
    }
    case 'password': {
      return (
        <input
          type="password"
          className="form-control"
          value={value ?? ''}
          placeholder={placeholder || '••••••••'}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'email': {
      return (
        <input
          type="email"
          className="form-control"
          value={value ?? ''}
          placeholder={placeholder || ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'url': {
      return (
        <input
          type="url"
          className="form-control"
          value={value ?? ''}
          placeholder={placeholder || 'https://'}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      );
    }
    case 'text':
    default: {
      const inputProps = {
        type: dataType === 'integer' || dataType === 'decimal' ? 'number' : 'text',
        className: 'form-control',
        value: value ?? '',
        placeholder: placeholder || '',
        onChange: (e) => onChange?.(e.target.value),
        disabled,
      };
      if (minLength != null) inputProps.minLength = minLength;
      if (maxLength != null) inputProps.maxLength = maxLength;
      if (pattern) inputProps.pattern = pattern;
      return <input {...inputProps} />;
    }
  }
}
